/**
 * EL ORDEN DE LOS CAMPOS EN LA FICHA DE LA PÓLIZA, ELEGIDO POR CADA AGENTE.
 *
 * Lissette, 2026-10-02: *"¿podemos hacer que los agentes puedan mover y ordenar
 * a su gusto los campos? Que los puedan arrastrar, colocarlos como quieran y
 * que se quede así. Una opción de editar las posiciones, que se vean que se
 * pueden mover, que se muevan y luego guarda."*
 *
 * - Es de CADA agente: se guarda en sus preferencias (`uiPrefs`), que le siguen
 *   de un equipo a otro. Lo que ordena una no le cambia la ficha a otra.
 * - Es por TIPO de póliza y por SECCIÓN de la ficha: el Medicare Advantage y el
 *   ACA no tienen los mismos campos.
 * - Sin orden guardado, la ficha sale exactamente como está programada.
 *
 * Aquí solo vive la lógica, sin React, para poder probarla.
 */

/**
 * La clave de una sección de un tipo de póliza dentro de
 * `agency_settings.policy_card_layout`: "<tipo>:<seccion>".
 *
 * Fue "deskte:ordenFicha:<tipo>:<seccion>" (la de cuando el orden era de cada
 * persona y vivía en sus preferencias). Al pasar a la agencia, el servidor
 * (utils/ordenDeLaFicha.ts) solo acepta "<tipo>:<seccion>" y DESCARTABA en
 * silencio todo lo demás: guardaba {} y contestaba 200, así que el orden se
 * veía guardado hasta recargar (Lissette 2026-10-22: "no se guarda"). Nunca
 * llegó a guardarse ninguno, así que no hay nada que migrar.
 */
export function claveDeOrden(tipo, seccion) {
  return `${tipo || 'otro'}:${seccion}`;
}

/** Lee un orden guardado; cualquier cosa rara se trata como "sin orden". */
export function leerOrden(texto) {
  if (!texto) return null;
  try {
    const v = JSON.parse(texto);
    return Array.isArray(v) && v.every((x) => typeof x === 'string') ? v : null;
  } catch {
    return null;
  }
}

/**
 * Pone los campos en el orden guardado.
 *
 * Un campo que el orden guardado no conoce (uno nuevo, o uno que en esta póliza
 * aparece y en la que se ordenó no) NO se pierde: se queda detrás del que tenía
 * delante en el orden de fábrica. Así un campo nuevo no desaparece ni salta al
 * final de la ficha.
 */
export function aplicarOrden(campos, orden) {
  if (!Array.isArray(orden) || !orden.length) return campos.slice();
  const pos = new Map(orden.map((c, i) => [c, i]));
  const conocidos = campos.filter((c) => pos.has(c)).sort((a, b) => pos.get(a) - pos.get(b));
  const salida = conocidos.slice();
  campos.forEach((c, i) => {
    if (pos.has(c)) return;
    // Detrás del primer campo conocido que tenía delante en el orden de fábrica.
    let ancla = -1;
    for (let j = i - 1; j >= 0; j--) {
      const k = salida.indexOf(campos[j]);
      if (k !== -1) { ancla = k; break; }
    }
    salida.splice(ancla + 1, 0, c);
  });
  return salida;
}

/** Mueve `campo` al sitio de `destino` (arrastrar y soltar encima de otro). */
export function moverCampo(lista, campo, destino) {
  const de = lista.indexOf(campo);
  const a = lista.indexOf(destino);
  if (de === -1 || a === -1 || de === a) return lista;
  const nueva = lista.slice();
  nueva.splice(de, 1);
  nueva.splice(a, 0, campo);
  return nueva;
}

/** Un paso hacia delante (-1) o hacia atrás (+1): para el teléfono, donde no se arrastra. */
export function pasoDeCampo(lista, campo, paso) {
  const de = lista.indexOf(campo);
  const a = de + paso;
  if (de === -1 || a < 0 || a >= lista.length) return lista;
  const nueva = lista.slice();
  [nueva[de], nueva[a]] = [nueva[a], nueva[de]];
  return nueva;
}
